import {
    AwesomeTableComponent,
    Button,
    DialogManager,
    IColumnsProps,
    Icon,
    InputTextSearch,
    Notifications,
    ObjectUtils,
    Progress,
    Select,
    useFirstTime,
} from "d-react-components";
import {
    debounce,
    filter,
    find,
    includes,
    isEmpty,
    map,
    toLower,
} from "lodash";
import { useContext, useEffect, useMemo, useRef, useState } from "react";
import MaterialAPI from "../../../api/queries/material";
import { ServiceConfigAPI } from "../../../api/queries/service";
import Drawer from "../../../common/Drawer";
import { SERVICE_ADJUSTMENT_CONFIGS } from "../../../constant/service";
import { ServiceConfigContext } from "../../../context/service";
import { IMaterial } from "../../../interfaces/material";
import Messages from "../../../languages/Messages";
import MaterialNameView from "../../material/share/MaterialNameView";
import QuantityInputField from "../../quotation/share/QuantityInputField";

interface IMaterialItem extends IMaterial {
    quantity: number;
    hasPermissionAdjustment: string;
}

interface IMaterialAddDrawer {
    open: boolean;
    onClose: () => void;
    defaultValue: IMaterialItem[];
}

interface ITreatmentMaterialUpdateDrawer {
    open: boolean;
    onClose: () => void;
    defaultValue: IMaterialItem;
}

const FILTER_TABS = [
    {
        id: "all",
        label: "all",
    },
    {
        id: "selected",
        label: "selected",
    },
];

const MaterialAddDrawer = ({
    open,
    onClose,
    defaultValue = [],
}: IMaterialAddDrawer) => {
    const { setConfiguration, configuration } =
        useContext(ServiceConfigContext);
    const [materialSelected, setMaterialSelected] =
        useState<IMaterialItem[]>(defaultValue);
    const [filterTab, setFilterTab] = useState("all");
    const searchRef = useRef<string>();
    const tableRef = useRef<any>();
    const isFirstTime = useFirstTime();

    useEffect(() => {
        if (isFirstTime) return;
        tableRef.current.refresh();
    }, [filterTab]);

    const onAddMaterial = () => {
        const materialFiltered = filter(
            materialSelected,
            (item) => !!item.quantity
        );
        const materials = map(materialFiltered, (item) => ({
            material: item.id,
            quantity: item.quantity,
            hasPermissionAdjustment: item.hasPermissionAdjustment,
        }));

        Progress.show(
            {
                method: ServiceConfigAPI.addMaterialPreset,
                params: [configuration?.id, materials],
            },
            (configRes: any) => {
                Notifications.showSuccess(Messages.addSuccess);
                setConfiguration(configRes);
                onClose();
            }
        );
    };

    const onChangeSearch = debounce((event) => {
        const search = event.target.value;
        searchRef.current = search;
        tableRef.current.refresh();
    }, 400);

    const columns: IColumnsProps = [
        {
            title: Messages.item,
            dataIndex: "",
            render: (material) => <MaterialNameView material={material} />,
        },
        {
            title: Messages.unit,
            dataIndex: "trackingUnit",
            render: (unit) => unit?.name,
        },
        {
            title: Messages.adjustAfterTreatment,
            dataIndex: "",
            render: (material: any) => {
                const itemSelected = find(
                    materialSelected,
                    (item) => item?.id === material?.id
                );
                if (!itemSelected) return <div />;
                return (
                    <Select
                        className="width-[75px]"
                        getLabel={(item) => Messages[item.label]}
                        value={itemSelected?.hasPermissionAdjustment}
                        dataSource={SERVICE_ADJUSTMENT_CONFIGS}
                        onChange={(value) =>
                            onChangeIsAdjust(value, itemSelected)
                        }
                    />
                );
            },
            width: 200,
        },
        {
            title: Messages.quantity,
            dataIndex: "",
            render: (material: any) => {
                const itemSelected = find(
                    materialSelected,
                    (item) => item?.id === material?.id
                );
                if (!itemSelected) return <div />;
                if (itemSelected?.hasPermissionAdjustment === "YES") {
                    return Messages.provideAfterTreatment;
                }
                return (
                    <QuantityInputField
                        quantity={itemSelected?.quantity}
                        onChangeQuantity={(quantity: number) =>
                            onChangeQuantity(quantity, itemSelected)
                        }
                        min={1}
                    />
                );
            },
            width: 200,
        },
    ];

    const onChangeQuantity = (quantity: number, material: IMaterial) => {
        const result = ObjectUtils.updateArrayById(materialSelected as any, {
            ...material,
            quantity,
        });
        setMaterialSelected(result);
    };

    const onChangeIsAdjust = (
        hasPermissionAdjustment: string,
        material: IMaterial & { quantity: number }
    ) => {
        const result = ObjectUtils.updateArrayById(materialSelected as any, {
            ...material,
            hasPermissionAdjustment,
            quantity:
                hasPermissionAdjustment === "YES" ? 0 : material?.quantity,
        });

        setMaterialSelected(result);
    };

    const source = (pagingData: any) => {
        if (filterTab === "selected") {
            return Promise.resolve({ data: materialSelected });
        }
        return MaterialAPI.search({
            page: pagingData?.pageIndex as any,
            limit: pagingData?.pageSize,
            search: searchRef?.current ?? "",
            sort: {
                createdAt: -1,
            },
        });
    };

    const onChangeSelectMaterial = (keys: string[], materials: IMaterial[]) => {
        const result = map(keys, (key) => {
            const itemSelected = find(materialSelected, (item) => {
                return !!item?.id && item?.id === key;
            });

            if (itemSelected) return itemSelected;
            const material = find(materials, (item) => item?.id === key);
            return { ...material, quantity: 1, hasPermissionAdjustment: "YES" };
        });
        setMaterialSelected(result as any);
    };

    return (
        <Drawer
            open={open}
            onClose={onClose}
            title={Messages.material}
            onSave={onAddMaterial}
            size="auto"
            width="80%"
        >
            <div className="p-4">
                <InputTextSearch onChange={onChangeSearch} />
                <div className="d-flex mt-3">
                    {map(FILTER_TABS, (item) => (
                        <Button
                            onClick={() => setFilterTab(item?.id)}
                            variant={
                                item.id === filterTab ? "standard" : "outline"
                            }
                            className="mr-2"
                            size="small"
                        >
                            {Messages[item.label]}
                        </Button>
                    ))}
                </div>
                <AwesomeTableComponent
                    className="mt-3"
                    ref={(ref) => {
                        tableRef.current = ref;
                    }}
                    columns={columns}
                    source={source}
                    getTotalItems={(res) => {
                        return res?.data?.data?.pagination?.items ?? 0;
                    }}
                    transformer={(res) => res?.data?.data?.data ?? []}
                    rowSelection={{
                        selectedRowKeys: map(
                            materialSelected,
                            (item) => item?.id
                        ),
                        preserveSelectedRowKeys: true,
                        onChange: onChangeSelectMaterial as any,
                    }}
                />
            </div>
        </Drawer>
    );
};

const TreatmentMaterialUpdateDrawer = ({
    open,
    onClose,
    defaultValue,
}: ITreatmentMaterialUpdateDrawer) => {
    const { setConfiguration, configuration } =
        useContext(ServiceConfigContext);
    const [material, setMaterial] = useState<IMaterialItem>(defaultValue);

    const onUpdateMaterialTreatment = () => {
        const input = {
            material: material?.id,
            quantity: material?.quantity,
            hasPermissionAdjustment: material?.hasPermissionAdjustment,
        };
        Progress.show(
            {
                method: ServiceConfigAPI.updateMaterialPreset,
                params: [configuration?.id, input],
            },
            (res: any) => {
                Notifications.showSuccess(Messages.updateSuccess);
                setConfiguration(res);
                onClose();
            }
        );
    };

    const columns: IColumnsProps = [
        {
            title: Messages.item,
            dataIndex: "",
            render: (material) => <MaterialNameView material={material} />,
        },
        {
            title: Messages.unit,
            dataIndex: "trackingUnit",
            render: (unit) => unit?.name,
        },
        {
            title: Messages.quantity,
            dataIndex: "quantity",
            render: (quantity: any, item) => {
                if (!item) return <div />;
                if (item?.hasPermissionAdjustment === "YES") {
                    return Messages.provideAfterTreatment;
                }
                return (
                    <QuantityInputField
                        quantity={quantity}
                        onChangeQuantity={(quantity: number) =>
                            setMaterial({ ...material, quantity })
                        }
                        min={1}
                    />
                );
            },
        },
    ];

    return (
        <Drawer
            open={open}
            onClose={onClose}
            title={Messages.material}
            onSave={onUpdateMaterialTreatment}
            size="auto"
            width="80%"
        >
            <div className="p-4">
                <AwesomeTableComponent
                    className="mt-3"
                    columns={columns}
                    dataSource={[material]}
                    pagination={false}
                />
                <Select
                    className="mt-3"
                    label={Messages.adjustAfterTreatment}
                    getLabel={(item) => Messages[item.label]}
                    value={material?.hasPermissionAdjustment}
                    dataSource={SERVICE_ADJUSTMENT_CONFIGS}
                    onChange={(hasPermissionAdjustment) =>
                        setMaterial({
                            ...material,
                            hasPermissionAdjustment,
                            quantity:
                                hasPermissionAdjustment === "YES"
                                    ? 0
                                    : material?.quantity,
                        })
                    }
                />
            </div>
        </Drawer>
    );
};

const ServiceConfigMaterial = () => {
    const { setConfiguration, configuration } =
        useContext(ServiceConfigContext);

    const [openAdd, setOpenAdd] = useState(false);
    const [updatingMaterial, setUpdatingMaterial] =
        useState<IMaterialItem | null>(null);
    const [textSearch, setTextSearch] = useState("");

    const columns: IColumnsProps = [
        {
            title: Messages.item,
            dataIndex: "material",
            render: (material: IMaterial) => (
                <MaterialNameView material={material} />
            ),
        },
        {
            title: Messages.unit,
            dataIndex: "material",
            render: (material: IMaterial) => material?.trackingUnit?.name,
        },
        {
            title: Messages.quantity,
            dataIndex: "quantity",
            width: 200,
            render: (quantity: any, item: any) => {
                if (item?.hasPermissionAdjustment === "YES") {
                    return Messages.provideAfterTreatment;
                }
                return quantity;
            },
        },
        {
            title: Messages.adjustAfterTreatment,
            dataIndex: "hasPermissionAdjustment",
            render: (isAdjust: string) => {
                const adjustConfig = find(
                    SERVICE_ADJUSTMENT_CONFIGS,
                    (item) => item?.id === isAdjust
                );
                return Messages[adjustConfig?.label as any];
            },
            width: 200,
        },
        {
            title: Messages.action,
            dataIndex: "",
            render: (material) => (
                <div className="d-flex">
                    <Icon
                        name="edit"
                        className="cursor-pointer mr-3 text-primary"
                        onClick={() =>
                            setUpdatingMaterial({
                                ...material.material,
                                quantity: material?.quantity,
                                hasPermissionAdjustment:
                                    material?.hasPermissionAdjustment,
                            })
                        }
                    />
                    <Icon
                        name="remove_circle_outline"
                        className="cursor-pointer text-primary"
                        onClick={() => onRemove(material)}
                    />
                </div>
            ),
        },
    ];

    const onRemove = (material: any) => {
        DialogManager.showConfirm(
            Messages.confirm,
            Messages.areYouSureWantRemove,
            () =>
                Progress.show(
                    {
                        method: ServiceConfigAPI.removeMaterialPreset,
                        params: [configuration?.id, material.material?.id],
                    },
                    (treatmentRes) => {
                        setConfiguration(treatmentRes);
                        Notifications.showSuccess(Messages.removeSuccess);
                    }
                )
        );
    };

    const onChangeSearch = debounce((event) => {
        const search = event.target.value;
        setTextSearch(search);
    }, 400);

    const materialDataSource = useMemo(() => {
        if (isEmpty(textSearch)) {
            return configuration?.materialPreset ?? [];
        }

        return filter(configuration?.materialPreset ?? [], (item) =>
            includes(
                toLower(item?.material?.name + item?.material?.sku),
                toLower(textSearch)
            )
        );
    }, [textSearch, configuration?.materialPreset]);

    return (
        <div className="mt-3">
            <div className="flex-row-between-center">
                <h5>{Messages.material}</h5>
                <Button onClick={() => setOpenAdd(true)} className="">
                    {Messages.add}
                </Button>
            </div>
            <InputTextSearch onChange={onChangeSearch} className="mt-3" />
            <AwesomeTableComponent
                columns={columns}
                dataSource={materialDataSource}
                className="mt-3"
                pagination={false}
            />
            {openAdd && (
                <MaterialAddDrawer
                    open={openAdd}
                    onClose={() => setOpenAdd(false)}
                    defaultValue={map(
                        configuration?.materialPreset,
                        (item) => ({
                            ...item.material,
                            quantity: item.quantity,
                            hasPermissionAdjustment:
                                item?.hasPermissionAdjustment,
                        })
                    )}
                />
            )}
            {updatingMaterial && (
                <TreatmentMaterialUpdateDrawer
                    defaultValue={updatingMaterial}
                    open={!!updatingMaterial}
                    onClose={() => setUpdatingMaterial(null)}
                />
            )}
        </div>
    );
};

export default ServiceConfigMaterial;
